import {
  createListCollection,
  Grid,
  HStack,
  Icon,
  IconButton,
  Input,
  Portal,
  Select,
  VStack,
} from '@chakra-ui/react'
import { Plus } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { ErrorState, LoadingState } from '@mangovoice/mango-ui'

import type { HotelsApi } from '../../api/hotelsApi'
import { HOTELS_LARGE_LIST_LIMIT } from '../../constants/limits'
import { useHotelsI18n } from '../../i18n'
import {
  useBulkCreateWakeupCalls,
  useDeleteWakeupCall,
  useListExtensionLines,
  useListRooms,
  useListWakeupCalls,
} from '../../queries/hooks'
import type { EnrichedRoom, HospitalityPermissions } from '../../types/rooms'
import type { WakeupCall } from '../../types/wakeup'
import {
  filterTimeOptions,
  useDateOptions,
  useTimeOptions,
} from '../../utils/timeOptions'
import { pbxWallTimeToUtcIso } from '../../utils/wakeupTimezone'
import { type PendingChange, PendingChangesTable } from './PendingChangesTable'
import { RoomSelectionTable } from './RoomSelectionTable'

interface WakeupSchedulerProps {
  api: HotelsApi
  pbxId: number
  pbxUuid: string
  /** IANA timezone for the PBX (from system settings `default_time_zone`, normalized). */
  pbxTimeZone: string
  toast: ToastLike
  permissions: HospitalityPermissions
}

export const WakeupScheduler = ({
  api,
  pbxId,
  pbxUuid,
  pbxTimeZone,
  toast,
  permissions,
}: WakeupSchedulerProps) => {
  const { text, locale } = useHotelsI18n()
  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<number>>(new Set())
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([])
  const [deletingId, setDeletingId] = useState<number | string | null>(null)
  const [timeFilter, setTimeFilter] = useState('')

  const dateOptions = useDateOptions({ locale, pbxTimeZone })
  const timeOptions = useTimeOptions(locale)

  const dateCollection = useMemo(
    () => createListCollection({ items: dateOptions }),
    [dateOptions],
  )

  const filteredTimeOptions = useMemo(
    () => filterTimeOptions(timeFilter, timeOptions),
    [timeFilter, timeOptions],
  )

  const timeCollection = useMemo(
    () => createListCollection({ items: filteredTimeOptions }),
    [filteredTimeOptions],
  )

  const extensionLinesQuery = useListExtensionLines({
    api,
    pbxId,
    options: { enabled: pbxId > 0, staleTime: 60_000 },
  })

  const roomsQuery = useListRooms({
    api,
    params: {
      pbx_uuid: pbxUuid,
      pbx_id: pbxId,
      limit: HOTELS_LARGE_LIST_LIMIT,
    },
    extensionLines: extensionLinesQuery.data,
  })

  const wakeupQuery = useListWakeupCalls({
    api,
    params: { pbx_id: pbxUuid },
    extensionLines: extensionLinesQuery.data,
  })

  const bulkCreate = useBulkCreateWakeupCalls({
    api,
    options: {
      onSuccess: (data) => {
        toast.create({
          title: text(
            'hotels.wakeupScheduler.scheduleSuccess',
            '{{count}} wake-up call{{suffix}} scheduled',
            {
              count: data.created_count,
              suffix: data.created_count !== 1 ? 's' : '',
            },
          ),
          type: 'success',
        })
        setPendingChanges([])
      },
      onError: () => {
        toast.create({
          title: text(
            'hotels.wakeupScheduler.scheduleFailed',
            'Failed to schedule wake-up calls',
          ),
          type: 'error',
        })
      },
    },
  })

  const deleteWakeup = useDeleteWakeupCall({
    api,
    options: {
      onSuccess: () => {
        toast.create({
          title: text(
            'hotels.wakeupScheduler.cancelSuccess',
            'Wake-up call canceled',
          ),
          type: 'success',
        })
        setDeletingId(null)
      },
      onError: () => {
        toast.create({
          title: text(
            'hotels.wakeupScheduler.cancelFailed',
            'Failed to cancel wake-up call',
          ),
          type: 'error',
        })
        setDeletingId(null)
      },
    },
  })

  const rooms: EnrichedRoom[] = useMemo(
    () =>
      Array.isArray(roomsQuery.data?.results) ? roomsQuery.data.results : [],
    [roomsQuery.data],
  )

  const activeWakeups: WakeupCall[] = useMemo(() => {
    const results = wakeupQuery.data?.results ?? []
    return results.filter(
      (wc: WakeupCall) =>
        wc.display_status === 'scheduled' || wc.display_status === 'tried',
    )
  }, [wakeupQuery.data])

  const roomMap = useMemo(() => new Map(rooms.map((r) => [r.id, r])), [rooms])
  const canCreateWakeups = !!permissions.canNewQueues
  const canDeleteWakeups =
    !!permissions.canDeleteQueues || !!permissions.canEditQueues

  const handleAdd = useCallback(() => {
    if (!canCreateWakeups) return
    if (!selectedDate || !selectedTime || selectedRoomIds.size === 0) return

    const newChanges: PendingChange[] = Array.from(selectedRoomIds)
      .map((roomId) => {
        const room = roomMap.get(roomId)
        if (!room) return null
        return {
          roomId: room.id,
          roomNumber: room.number,
          roomName: room.name,
          extensionUuid: room.extension_uuid,
          date: selectedDate,
          time: selectedTime,
        }
      })
      .filter(Boolean) as PendingChange[]

    setPendingChanges((prev) => [...prev, ...newChanges])
    setSelectedRoomIds(new Set())
  }, [canCreateWakeups, selectedDate, selectedTime, selectedRoomIds, roomMap])

  const handleApply = useCallback(() => {
    if (!canCreateWakeups) return
    if (pendingChanges.length === 0) return

    const wakeupCalls: {
      pbx_uuid: string
      extension_uuid: string
      wuc_timestamp: string
    }[] = []
    for (const change of pendingChanges) {
      const wuc_timestamp = pbxWallTimeToUtcIso(
        change.date,
        change.time,
        pbxTimeZone,
      )
      if (!wuc_timestamp) {
        toast.create({
          title: text(
            'hotels.wakeupScheduler.invalidTimeRoom',
            'Room {{room}}: that time does not exist on the selected date (DST transition). Please choose a different time.',
            { room: change.roomNumber },
          ),
          type: 'warning',
        })
        return
      }
      wakeupCalls.push({
        pbx_uuid: pbxUuid,
        extension_uuid: change.extensionUuid,
        wuc_timestamp,
      })
    }

    bulkCreate.mutate({ wakeup_calls: wakeupCalls })
  }, [
    canCreateWakeups,
    pendingChanges,
    pbxUuid,
    pbxTimeZone,
    bulkCreate,
    toast,
    text,
  ])

  const handleRemovePending = useCallback((index: number) => {
    setPendingChanges((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleResetPending = useCallback(() => {
    setPendingChanges([])
  }, [])

  const handleDeleteWakeup = useCallback(
    (id: number | string) => {
      if (!canDeleteWakeups) return
      setDeletingId(id)
      deleteWakeup.mutate({ id })
    },
    [canDeleteWakeups, deleteWakeup],
  )

  const selectedTimeLabel = useMemo(
    () => timeOptions.find((o) => o.value === selectedTime)?.label,
    [timeOptions, selectedTime],
  )

  if (roomsQuery.isLoading)
    return (
      <LoadingState
        title=""
        size="lg"
        ariaLabel={text('hotels.common.loading', 'Loading')}
      />
    )
  if (roomsQuery.isError) {
    return (
      <ErrorState
        title={text('hotels.errors.loadRooms', 'Failed to load rooms')}
        retryLabel={text('hotels.common.retry', 'Try again')}
        onRetry={() => roomsQuery.refetch()}
      />
    )
  }

  return (
    <VStack align="stretch" gap={4}>
      {/* Top toolbar: day/time dropdowns + add button */}
      <HStack gap={2}>
        <Select.Root
          collection={dateCollection}
          size="sm"
          width="140px"
          disabled={!canCreateWakeups}
          value={selectedDate ? [selectedDate] : []}
          onValueChange={(e) => setSelectedDate(e.value[0] ?? '')}
        >
          <Select.Label srOnly>
            {text('hotels.wakeupScheduler.dayLabel', 'Day')}
          </Select.Label>
          <Select.Trigger borderRadius="l2">
            <Select.ValueText
              placeholder={text('hotels.common.dayPlaceholder', 'Day...')}
            />
          </Select.Trigger>
          <Portal>
            <Select.Positioner>
              <Select.Content>
                {dateCollection.items.map((opt) => (
                  <Select.Item key={opt.value} item={opt}>
                    {opt.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>

        <Select.Root
          collection={timeCollection}
          size="sm"
          width="160px"
          disabled={!canCreateWakeups}
          value={selectedTime ? [selectedTime] : []}
          onValueChange={(e) => {
            setSelectedTime(e.value[0] ?? '')
            setTimeFilter('')
          }}
          onOpenChange={(e) => {
            if (!e.open) setTimeFilter('')
          }}
        >
          <Select.Label srOnly>
            {text('hotels.wakeupScheduler.timeLabel', 'Time')}
          </Select.Label>
          <Select.Trigger borderRadius="l2">
            <Select.ValueText>
              {selectedTimeLabel ??
                text('hotels.common.timePlaceholder', 'Time...')}
            </Select.ValueText>
          </Select.Trigger>
          <Portal>
            <Select.Positioner>
              <Select.Content>
                <Input
                  size="sm"
                  borderRadius="l2"
                  aria-label={text(
                    'hotels.wakeupScheduler.filterTimesLabel',
                    'Filter times',
                  )}
                  placeholder={text(
                    'hotels.common.filterTimesPlaceholder',
                    'Filter times...',
                  )}
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  mx={1}
                  my={1}
                  width="calc(100% - 8px)"
                />
                {timeCollection.items.map((opt) => (
                  <Select.Item key={opt.value} item={opt}>
                    {opt.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>

        <IconButton
          aria-label={text(
            'hotels.wakeupScheduler.addToPendingAria',
            'Add to pending changes',
          )}
          size="sm"
          borderRadius="l2"
          variant="solid"
          colorPalette="brand"
          color="fg.inverted"
          _disabled={{ color: 'fg.inverted' }}
          onClick={handleAdd}
          disabled={
            !canCreateWakeups ||
            !selectedDate ||
            !selectedTime ||
            selectedRoomIds.size === 0 ||
            bulkCreate.isPending
          }
        >
          <Icon as={Plus} boxSize={5} color="fg.inverted" />
        </IconButton>
      </HStack>

      {/* Two-column layout: room table (left) + pending changes (right) */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
        <RoomSelectionTable
          rooms={rooms}
          wakeupCalls={activeWakeups}
          selectedRoomIds={selectedRoomIds}
          onSelectionChange={setSelectedRoomIds}
          onDeleteWakeup={handleDeleteWakeup}
          isDeletingId={deletingId}
          canStageWakeups={canCreateWakeups}
          canDeleteWakeups={canDeleteWakeups}
          pbxTimeZone={pbxTimeZone}
          locale={locale}
        />

        <PendingChangesTable
          changes={pendingChanges}
          onRemove={handleRemovePending}
          onApply={handleApply}
          onReset={handleResetPending}
          isApplying={bulkCreate.isPending}
          canApplyChanges={canCreateWakeups}
          pbxTimeZone={pbxTimeZone}
          locale={locale}
        />
      </Grid>
    </VStack>
  )
}
